import { useMemo, useState, useContext, createContext, PropsWithChildren } from 'react';

import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

import { FeatureType } from 'src/types/subscription.types';
import { NoteFindings, useGetNoteFindingsQuery } from 'src/services/ai/ai-enrichment';

import Iconify from 'src/components/iconify';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { FindingsList } from './findings-list';

/**
 * "N suggestions from this note" (JT1-1767). The notes list asks once for every note on the page
 * and shares the answer through this context, rather than each note asking for itself. The answer
 * refreshes when a review finishes, through the `ai_note_reviewed` push (see the notifications
 * popover), so a just-saved note gains its label without a page refresh.
 */

const NoteFindingsContext = createContext<Map<string, NoteFindings> | null>(null);

export function NoteFindingsProvider({ commentIds, children }: PropsWithChildren<{ commentIds: string[] }>) {
  const { canAccess } = useFeatures();
  const enabled = canAccess(FeatureType.AI_SOURCING_ENABLED);
  const ids = useMemo(() => [...commentIds].sort(), [commentIds]);
  const { currentData } = useGetNoteFindingsQuery(ids, { skip: !enabled || !ids.length });

  const byComment = useMemo(() => new Map((currentData ?? []).map((findings) => [findings.comment_id, findings])), [currentData]);
  return <NoteFindingsContext.Provider value={byComment}>{children}</NoteFindingsContext.Provider>;
}

export function NoteFindingsChip({ commentId }: { commentId: string }) {
  const findings = useContext(NoteFindingsContext)?.get(commentId);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const pending = (findings?.suggestions ?? []).filter((s) => s.status === 'pending');
  const filled = (findings?.suggestions ?? []).filter((s) => s.status === 'auto_applied');
  const tasks = (findings?.tasks ?? []).filter((t) => t.status === 'pending');
  const count = pending.length + tasks.length;
  // A note whose findings were all decided, or that only filled empty fields, shows the fills.
  if (!findings || (!count && !filled.length)) return null;

  const label = count
    ? `${count} suggestion${count === 1 ? '' : 's'} from this note`
    : `AI filled ${filled.length} field${filled.length === 1 ? '' : 's'} from this note`;

  return (
    <>
      <Chip
        size="small"
        variant="soft"
        color={count ? 'info' : 'default'}
        icon={<Iconify icon="mdi:robot-outline" width={16} />}
        label={label}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ alignSelf: 'flex-start', mt: 1 }}
      />
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 360, maxWidth: '90vw' } } }}
      >
        {count > 0 && <FindingsList suggestions={pending} tasks={tasks} />}
        {filled.length > 0 && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: count ? 1 : 0 }}>
            Filled in automatically: {filled.map((f) => f.label).join(', ')}.
          </Typography>
        )}
      </Popover>
    </>
  );
}
